import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { formatLanguages } from "../../utils/helpers";

const THUMB_COLORS = {
  "react portfolio":    { bg: "#E8F0E9", text: "#3D5C42" },
  "paw shop":           { bg: "#FFF0E8", text: "#7A3B1E" },
  "astrosounds":        { bg: "#EEE8F5", text: "#4A2D7A" },
  "weather dashboard":  { bg: "#E8EFF5", text: "#1A3D5C" },
  "my medication list": { bg: "#F5EDE8", text: "#5C3420" },
  "ecommerce backend":  { bg: "#1A1714", text: "#FAF8F4" },
  "employee tracker":   { bg: "#E8F5EE", text: "#1A4A30" },
  "readme generator":   { bg: "#F5F0E8", text: "#5C4A1A" },
  "social network backend": { bg: "#EEE8F5", text: "#3A1A5C" },
};
const DEFAULT_THUMB = { bg: "#F0EDE8", text: "#6B6560" };

function getThumb(title) {
  const key = (title || "").toLowerCase();
  for (const [k, v] of Object.entries(THUMB_COLORS)) {
    if (key.includes(k)) return v;
  }
  return DEFAULT_THUMB;
}

const BACKEND_KEYWORDS = ["python", "django", "node", "express", "sql", "mysql", "postgresql", "backend", "mongodb"];

const FILTERS = {
  All: () => true,
  React: (p) => p.languages.some((l) => l.name.toLowerCase().includes("react")),
  Backend: (p) => p.languages.some((l) => BACKEND_KEYWORDS.some((k) => l.name.toLowerCase().includes(k))),
  Tools: (p) =>
    !p.languages.some((l) => l.name.toLowerCase().includes("react")) &&
    !p.languages.some((l) => BACKEND_KEYWORDS.some((k) => l.name.toLowerCase().includes(k))),
};

function Projects() {
  const [projects, setProjects] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeFilter, setActiveFilter] = useState("All");

  useEffect(() => {
    fetch("/api/projects/")
      .then((res) => { if (!res.ok) throw new Error(`API error: ${res.status}`); return res.json(); })
      .then((data) => { setProjects(data); setLoading(false); })
      .catch((err) => { setError(err.message); setLoading(false); });
  }, []);

  const filtered = projects.filter(FILTERS[activeFilter] || (() => true));

  return (
    <div className="projects-page">
      <div className="projects-header">
        <h1>Projects</h1>
        <div className="filter-bar">
          {Object.keys(FILTERS).map((f) => (
            <button
              key={f}
              className={`filter-btn${activeFilter === f ? " active" : ""}`}
              onClick={() => setActiveFilter(f)}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      {loading && <p style={{ color: "var(--ww-muted)", fontSize: 14 }}>Loading projects...</p>}
      {error && <p style={{ color: "var(--ww-alert)", fontSize: 14 }}>{error}</p>}

      <div className="projects-grid">
        {filtered.map((project) => {
          const { bg, text } = getThumb(project.title);
          return (
            <div key={project.id} className="project-card" onClick={() => setSelected(project)}>
              <div className="project-thumb" style={{ background: bg }}>
                <span className="project-thumb-text" style={{ color: text }}>{project.title}</span>
              </div>
              <div className="project-info">
                <p className="project-title">{project.title}</p>
                <div className="project-tags">
                  {project.languages?.map((l) => (
                    <span key={l.id} className="project-tag">{l.name}</span>
                  ))}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {selected && createPortal(
        <div className="project-modal-overlay" onClick={() => setSelected(null)}>
          <div className="project-modal" onClick={(e) => e.stopPropagation()}>
            <button className="project-modal__close" onClick={() => setSelected(null)}>×</button>
            <h2>{selected.title}</h2>
            {selected.image && <img alt={selected.title} src={selected.image} />}
            <p>{selected.description}</p>
            {selected.languages?.length > 0 && (
              <p className="project-modal__langs">{formatLanguages(selected.languages)}</p>
            )}
            <div className="project-modal__links">
              {selected.deployment && (
                <a href={selected.deployment} className="project-modal__link" target="_blank" rel="noreferrer">
                  Deployment ↗
                </a>
              )}
              {selected.repository && (
                <a href={selected.repository} className="project-modal__link" target="_blank" rel="noreferrer">
                  Repository ↗
                </a>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

export default Projects;
